import moment from 'moment-timezone';

import { rewindConfig } from 'components/rewind';
import { channelAttributes } from 'utils/channels';

import { RewindDataNormalized } from '../hooks';

const getChannelName = (channel: string): string => {
  const attributes =
    channelAttributes[channel as keyof typeof channelAttributes];
  return attributes?.name ?? channel.replace(/_/g, ' ');
};

const formatHour = (hour: number): string => {
  return moment().hour(hour).minute(0).format('LT');
};

const getCsatEmoji = (score: number | undefined | null): string => {
  if (!score) return '✨';
  if (score >= 90) return '👑';
  if (score >= 80) return '🌟';
  if (score >= 70) return '💫';
  if (score >= 60) return '⭐';
  return '🚀';
};

const getMonthName = (month: number | undefined | null): string => {
  if (month === undefined || month === null || month < 1 || month > 12)
    return 'Unknown';
  // API returns 1 = January, moment.months expects 0 = January
  return moment.months(month - 1) ?? 'Unknown';
};

const getHoursFromMs = (ms: number | undefined | null): number => {
  return Math.round((ms ?? 0) / (1000 * 60 * 60));
};

/** Generates engaging shareable summary of Rewind. */
export function getRewindSummary(data: RewindDataNormalized): string {
  const totalConversations = data.total_conversations_handled.agent;
  const uniqueCustomersHelped = data.unique_customers_helped.agent;
  const totalMessagesSent = data.total_messages_sent.agent;
  const hoursSpentHelping = getHoursFromMs(
    data.total_time_spent_helping_ms.agent,
  );
  const pages = Math.round((totalMessagesSent * 20) / 250);
  const days = (hoursSpentHelping / 24).toFixed(1);

  const topChannel = data.channel_breakdown[0];
  const busiestMonth = getMonthName(data.busiest_month?.month);
  const mostActiveHour = data.most_active_hour;

  const csatScore = data.csat_score?.agent;
  const ratingsReceived = data.total_ratings_received?.agent;
  const fiveStarRatingsCount = data.five_star_ratings_count?.agent;

  const lines: string[] = [
    `My ${rewindConfig.hashtag} is here! 🚀`,
    ``,
    `👥 ${totalConversations.toLocaleString()} conversations with ${uniqueCustomersHelped.toLocaleString()} customers`,
    `💬 ${totalMessagesSent.toLocaleString()} messages sent - enough to fill a ${pages.toLocaleString()}-page book`,
    `⏱️ ${hoursSpentHelping.toLocaleString()} hours spent helping people (${days} full days!)`,
    ``,
    `${topChannel ? getChannelName(topChannel.channel) : 'Multi-channel'} was my top channel. ${busiestMonth} was my busiest month. ${formatHour(mostActiveHour)} was when I hit my stride.`,
  ];

  // Add CSAT line if available
  if (csatScore !== undefined && csatScore !== null && ratingsReceived > 0) {
    const csatEmoji = getCsatEmoji(csatScore);
    const fiveStarPercentage =
      fiveStarRatingsCount > 0
        ? Math.round((fiveStarRatingsCount / ratingsReceived) * 100)
        : 0;

    lines.push(``);
    lines.push(
      `${csatEmoji} ${csatScore}% CSAT — ${ratingsReceived.toLocaleString()} ratings${fiveStarPercentage > 0 ? `, ${fiveStarPercentage}% five-star 🌟` : ''}`,
    );
  }

  lines.push(``);
  lines.push(`Bring on ${rewindConfig.year + 1}!`);

  return lines.join('\n');
}
